import React from "react";

const medalIcons = {
  gold: "🥇",
  silver: "🥈",
  bronze: "🥉",
};

interface Score {
  position: number;
  alias: string;
  score: number;
}

interface ScoresTableProps {
  title: string;
  description: string;
  scores: Score[];
  titleClassName?: string;
  descriptionClassName?: string;
  tableClassName?: string;
  maxPositions?: number;
  layout?: "vertical" | "columns";
  withGradientHeader?: boolean;
  gradientColors?: string;
}

export const ScoresTable: React.FC<ScoresTableProps> = ({
  title,
  description,
  scores,
  titleClassName,
  descriptionClassName,
  tableClassName,
  maxPositions = 10,
  layout = "vertical",
  withGradientHeader = false,
  gradientColors = "linear-gradient(180deg, #7B1FA2 -54.74%, #292E33 99.63%)",
}) => {
  const displayedScores =
    scores.length === 0
      ? [
          { position: 1, alias: "-", score: 0 },
          { position: 2, alias: "-", score: 0 },
          { position: 3, alias: "-", score: 0 },
        ]
      : scores.slice(0, maxPositions);
  const half = Math.ceil(displayedScores.length / 2);

  return (
    <div
      className={`flex flex-col h-full font-play gap-4 opacity-1 bg-[#292E33] ${withGradientHeader ? "rounded-tr-[40px] rounded-tl-[8px] rounded-bl-[8px] rounded-br-[8px]" : "rounded-[8px]"} ${tableClassName}`}
    >
      <div
        className={`p-4 ${withGradientHeader ? "rounded-tr-[40px] rounded-tl-[8px] rounded-bl-[8px] rounded-br-[8px]" : "rounded-[8px]"}`}
        style={withGradientHeader ? { background: gradientColors } : {}}
      >
        <h2 className={titleClassName}>{title}</h2>
        <p className={descriptionClassName}>{description}</p>
      </div>
      <div className="flex-1 overflow-auto p-4">
        {layout === "vertical" ? (
          <table className="w-full table-auto text-left tracking-[-0.006em]">
            <tbody>
              {displayedScores.map((score, index) => {
                let medal = "";
                if (index === 0) medal = medalIcons.gold;
                else if (index === 1) medal = medalIcons.silver;
                else if (index === 2) medal = medalIcons.bronze;

                return (
                  <tr key={index} className="text-center">
                    <td className="w-[50px] p-2">{medal}</td>
                    <td className="w-[50px] p-2">{score.position}°</td>
                    <td className="w-[150px] p-2">{score.alias}</td>
                    <td className="w-[80px] p-2 font-bold">{score.score}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        ) : (
          <div className="flex">
            <table className="w-1/2 table-auto text-left tracking-[-0.006em] mr-8">
              <tbody>
                {displayedScores.slice(0, half).map((score, index) => {
                  let medal = "";
                  if (index === 0) medal = medalIcons.gold;
                  else if (index === 1) medal = medalIcons.silver;
                  else if (index === 2) medal = medalIcons.bronze;

                  return (
                    <tr key={index} className="text-center">
                      <td className="w-[50px] p-2">{medal}</td>
                      <td className="w-[50px] p-2">{score.position}°</td>
                      <td className="w-[150px] p-2">{score.alias}</td>
                      <td className="w-[80px] p-2 font-bold">{score.score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <table className="w-1/2 table-auto text-left tracking-[-0.006em] ml-8">
              <tbody>
                {displayedScores.slice(half).map((score, index) => {
                  return (
                    <tr key={index} className="text-center">
                      <td className="w-[10px] p-2"></td>
                      <td className="w-[50px] p-2">{score.position}°</td>
                      <td className="w-[150px] p-2">{score.alias}</td>
                      <td className="w-[80px] p-2 font-bold">{score.score}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
